import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Trash2, Mail, Loader2, Calendar, User as UserIcon, X, Send } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import SavedEmailsPicker from "@/components/SavedEmailsPicker";
import { operationsApi, clientsApi, adminApi } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useBranding } from "@/contexts/BrandingContext";
import { buildOperationHtml } from "@/lib/pdfTemplate";
import { toImageDataUrl } from "@/lib/image";

const labelMap = {
  DEPOT: "Dépôt",
  RESTITUTION: "Restitution",
  DELIVRANCE: "Délivrance",
};

const subjectMap = {
  DEPOT: "Bordereau de dépôt",
  RESTITUTION: "Bordereau de restitution",
  DELIVRANCE: "Bordereau de délivrance",
};

const backupEmailFieldMap = {
  DEPOT: "email_depot",
  RESTITUTION: "email_restitution",
  DELIVRANCE: "email_delivrance",
};

const fmt = (d) => {
  try {
    return new Date(d).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

export default function OperationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const branding = useBranding();
  const [op, setOp] = useState(null);
  const [client, setClient] = useState(null);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  // Resend email state
  const [resendOpen, setResendOpen] = useState(false);
  const [resendRecipient, setResendRecipient] = useState("");
  const [resendToClient, setResendToClient] = useState(false);
  const [resendExtras, setResendExtras] = useState([]);
  const [resendBusy, setResendBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const operation = await operationsApi.get(id);
        if (cancelled) return;
        setOp(operation);
        const mediaList = await operationsApi.getMedia(id);
        if (cancelled) return;
        setMedia(mediaList);
        if (operation.client_id) {
          try {
            const cl = await clientsApi.get(operation.client_id);
            if (!cancelled) setClient(cl);
          } catch (err) {
            // Client may have been permanently deleted — keep going with the snapshot
            console.warn("[OperationDetail] client fetch failed", err);
          }
        }
      } catch (err) {
        toast.error("Opération introuvable");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // `operationsApi`/`clientsApi` are imported singletons, all the setters
    // are stable, and the local `cancelled` flag is intentionally excluded
    // (including it would cause an infinite loop). `id` is the only real
    // input that should re-fire the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleDelete = async () => {
    if (!window.confirm("Supprimer définitivement cette opération et ses médias ?")) return;
    setDeleting(true);
    try {
      await operationsApi.remove(id);
      toast.success("Opération supprimée");
      navigate("/recherche");
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleting(false);
    }
  };

  // Open the resend panel + pre-fill archive email for this operation type
  const handleResendOpen = async () => {
    setResendOpen(true);
    setResendToClient(false);
    setResendExtras([]);
    setResendRecipient("");
    try {
      const data = await adminApi.backupEmails();
      const field = op?.type ? backupEmailFieldMap[op.type] : null;
      setResendRecipient((field && data?.[field]) || "");
    } catch (err) {
      // Non-blocking — user can type the recipient manually
      console.warn("[OperationDetail] backup-emails fetch failed", err);
    }
  };

  // Aggregated recipients (dedupe case-insensitive)
  const resendRecipientsList = (() => {
    const all = [];
    if (resendRecipient) all.push(resendRecipient.trim());
    if (resendToClient && client?.email) all.push(client.email.trim());
    for (const em of resendExtras) {
      if (em && em.trim()) all.push(em.trim());
    }
    const seen = new Set();
    return all.filter((r) => {
      if (!r) return false;
      const k = r.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  })();

  const handleResendSend = async () => {
    if (resendRecipientsList.length === 0 || !op) return;
    if (op.purged) {
      toast.error("Médias purgés — impossible de regénérer le PDF");
      return;
    }
    setResendBusy(true);
    try {
      const photoSrcs = media
        .filter((m) => m.type === "PHOTO")
        .map((m) => toImageDataUrl(m.base64_data, "image/jpeg"));
      const signatureSrc = (() => {
        const s = media.find((m) => m.type === "SIGNATURE");
        return s ? toImageDataUrl(s.base64_data, "image/png") : null;
      })();

      const html = buildOperationHtml({
        operation: op,
        client,
        photos: photoSrcs,
        signature: signatureSrc,
        branding,
        utilisateur: op.utilisateur || user?.username || "",
      });

      const clientName = client
        ? `${client.nom}${client.prenom ? " " + client.prenom : ""}`
        : op.client_name_snapshot || "Client";
      const subject = `${subjectMap[op.type] || op.type} - ${clientName} - N° ${op.id}`;

      const result = await operationsApi.sendEmail(op.id, {
        html,
        recipients: resendRecipientsList,
        subject,
        operation_type: op.type,
        client_name: clientName,
        sender_username: user?.username,
      });
      toast.success("Email renvoyé avec succès", {
        description:
          resendRecipientsList.length === 1
            ? `Le PDF a été envoyé à ${resendRecipientsList[0]}`
            : `Le PDF a été envoyé à ${resendRecipientsList.length} destinataires`,
      });
      // Refresh op state so the "Email envoyé" line reflects the latest send
      setOp((prev) => ({
        ...prev,
        email_sent: true,
        email_sent_to: result?.recipients?.join(", ") || resendRecipientsList.join(", "),
        email_sent_at: new Date().toISOString(),
      }));
      setResendOpen(false);
    } catch (err) {
      toast.error("Erreur lors du renvoi", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setResendBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-[var(--vidoc-text-secondary)]">Chargement...</div>
    );
  }
  if (!op) return null;

  const photos = media.filter((m) => m.type === "PHOTO");
  const signature = media.find((m) => m.type === "SIGNATURE");
  const clientName = client
    ? `${client.nom}${client.prenom ? " " + client.prenom : ""}`
    : op.client_name_snapshot || "Client inconnu";

  return (
    <div data-testid="operation-detail-page">
      <PageHeader
        title={`${labelMap[op.type] || op.type} N° ${op.id}`}
        subtitle={clientName}
        backTo={-1}
        right={
          <button
            data-testid="operation-delete-btn"
            onClick={handleDelete}
            disabled={deleting}
            className="p-2 rounded-full hover:bg-red-50 text-[var(--vidoc-danger)] touch-target"
          >
            {deleting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Trash2 className="w-5 h-5" />}
          </button>
        }
      />

      <div className="px-4 md:px-8 py-6 max-w-3xl space-y-6">
        <div className="bg-white rounded-xl border border-[var(--vidoc-border)] divide-y divide-[var(--vidoc-border)]">
          <Row icon={Calendar} label="Date" value={fmt(op.date)} />
          <Row icon={UserIcon} label="Utilisateur" value={op.utilisateur} />
          {op.signatory_name && (
            <Row icon={UserIcon} label="Signataire" value={op.signatory_name} />
          )}
          {op.email_sent && (
            <Row
              icon={Mail}
              label="Email envoyé"
              value={`${op.email_sent_to || ""} · ${fmt(op.email_sent_at)}`}
            />
          )}
        </div>

        {op.observations && (
          <section>
            <h2 className="text-sm font-semibold mb-2">Observations</h2>
            <div className="bg-white rounded-xl border border-[var(--vidoc-border)] p-4 whitespace-pre-wrap text-sm">
              {op.observations}
            </div>
          </section>
        )}

        {/* Resend by email */}
        {!op.purged && (
          <section data-testid="operation-resend-section">
            {!resendOpen ? (
              <button
                data-testid="operation-resend-btn"
                type="button"
                onClick={handleResendOpen}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white border border-[var(--vidoc-primary)] text-[var(--vidoc-primary)] font-semibold touch-target hover:bg-[var(--vidoc-bg-secondary)]"
              >
                <Send className="w-4 h-4" />
                {op.email_sent ? "Renvoyer le bordereau par email" : "Envoyer le bordereau par email"}
              </button>
            ) : (
              <div className="bg-white rounded-xl border border-[var(--vidoc-border)] p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold">
                    {op.email_sent ? "Renvoyer ce bordereau" : "Envoyer ce bordereau"}
                  </h2>
                  <button
                    type="button"
                    data-testid="operation-resend-cancel"
                    onClick={() => setResendOpen(false)}
                    disabled={resendBusy}
                    className="p-1.5 rounded-lg hover:bg-[var(--vidoc-bg-secondary)]"
                    aria-label="Fermer"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Primary recipient */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-[var(--vidoc-text-secondary)]">
                    Destinataire principal
                  </label>
                  <input
                    data-testid="resend-recipient-input"
                    type="email"
                    value={resendRecipient}
                    onChange={(e) => setResendRecipient(e.target.value)}
                    placeholder="exemple@maison.com"
                    className="w-full px-4 py-3 rounded-xl bg-[var(--vidoc-bg-secondary)] border border-transparent focus:border-[var(--vidoc-primary)] focus:bg-white outline-none touch-target"
                  />
                </div>

                {/* Send to client toggle */}
                <label
                  className={`flex items-start gap-3 px-4 py-3 rounded-xl border ${
                    client?.email
                      ? "bg-[var(--vidoc-bg-secondary)] border-[var(--vidoc-border)] cursor-pointer"
                      : "bg-[var(--vidoc-bg-secondary)]/40 border-dashed border-[var(--vidoc-border)] opacity-70"
                  }`}
                >
                  <input
                    data-testid="resend-send-to-client-toggle"
                    type="checkbox"
                    checked={resendToClient && Boolean(client?.email)}
                    disabled={!client?.email}
                    onChange={(e) => setResendToClient(e.target.checked)}
                    className="mt-1 w-5 h-5 accent-[var(--vidoc-primary)] touch-target"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">Envoyer aussi au client</div>
                    <div className="text-xs text-[var(--vidoc-text-secondary)] truncate">
                      {client?.email
                        ? `${client.email} — cochez si vous souhaitez l'inclure`
                        : "Aucun email enregistré sur la fiche client"}
                    </div>
                  </div>
                </label>

                {/* Saved emails picker — has its own master toggle */}
                <SavedEmailsPicker
                  selectedEmails={resendExtras}
                  onSelectionChange={setResendExtras}
                />

                <div
                  data-testid="resend-recipients-summary"
                  className="text-xs text-[var(--vidoc-text-secondary)] px-1"
                >
                  {resendRecipientsList.length === 0
                    ? "Aucun destinataire — renseignez au moins une adresse"
                    : `Le PDF sera envoyé à : ${resendRecipientsList.join(", ")}`}
                </div>

                <button
                  data-testid="resend-send-btn"
                  type="button"
                  onClick={handleResendSend}
                  disabled={resendRecipientsList.length === 0 || resendBusy}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[var(--vidoc-primary)] text-white font-semibold touch-target disabled:opacity-50 hover:bg-[var(--vidoc-primary-dark)]"
                >
                  {resendBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {resendRecipientsList.length > 1
                    ? `Renvoyer le PDF (${resendRecipientsList.length} destinataires)`
                    : "Renvoyer le PDF"}
                </button>
              </div>
            )}
          </section>
        )}

        {op.purged && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
            Les médias de cette opération ont été purgés. Le PDF reste disponible via l'email envoyé.
          </div>
        )}

        {!op.purged && photos.length > 0 && (
          <section>
            <h2 className="text-sm font-semibold mb-2">Photos ({photos.length})</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {photos.map((m, idx) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setLightboxIdx(idx)}
                  className="aspect-square rounded-lg overflow-hidden bg-[var(--vidoc-bg-secondary)] block touch-target"
                  data-testid={`operation-photo-${m.id}`}
                >
                  <img
                    src={toImageDataUrl(m.base64_data, "image/jpeg")}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </section>
        )}

        {!op.purged && signature && (
          <section>
            <h2 className="text-sm font-semibold mb-2">Signature</h2>
            <div className="bg-white rounded-xl border border-[var(--vidoc-border)] p-4 flex justify-center">
              <img
                src={toImageDataUrl(signature.base64_data, "image/png")}
                alt="Signature"
                className="max-h-32"
              />
            </div>
          </section>
        )}
      </div>

      {lightboxIdx != null && photos[lightboxIdx] && (
        <Lightbox
          photo={photos[lightboxIdx]}
          index={lightboxIdx}
          total={photos.length}
          onClose={() => setLightboxIdx(null)}
          onPrev={() =>
            setLightboxIdx((i) => (i > 0 ? i - 1 : photos.length - 1))
          }
          onNext={() =>
            setLightboxIdx((i) => (i < photos.length - 1 ? i + 1 : 0))
          }
        />
      )}
    </div>
  );
}

function Lightbox({ photo, index, total, onClose, onPrev, onNext }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && total > 1) onPrev();
      else if (e.key === "ArrowRight" && total > 1) onNext();
    };
    document.addEventListener("keydown", onKey);
    // Lock body scroll
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, onPrev, onNext, total]);

  return (
    <div
      data-testid="photo-lightbox"
      className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center animate-slideUp"
      onClick={onClose}
    >
      <button
        type="button"
        data-testid="lightbox-close"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/15 text-white flex items-center justify-center hover:bg-white/25 touch-target"
        aria-label="Fermer"
      >
        <X className="w-6 h-6" />
      </button>
      <div className="absolute top-4 left-4 text-white text-sm font-medium px-3 py-1 rounded-full bg-white/15">
        {index + 1} / {total}
      </div>
      <img
        src={toImageDataUrl(photo.base64_data, "image/jpeg")}
        alt=""
        className="max-w-[95vw] max-h-[90vh] object-contain"
        onClick={(e) => e.stopPropagation()}
      />
      {total > 1 && (
        <>
          <button
            type="button"
            data-testid="lightbox-prev"
            onClick={(e) => {
              e.stopPropagation();
              onPrev();
            }}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/15 text-white text-2xl flex items-center justify-center hover:bg-white/25 touch-target"
            aria-label="Précédent"
          >
            ‹
          </button>
          <button
            type="button"
            data-testid="lightbox-next"
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/15 text-white text-2xl flex items-center justify-center hover:bg-white/25 touch-target"
            aria-label="Suivant"
          >
            ›
          </button>
        </>
      )}
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <Icon className="w-4 h-4 text-[var(--vidoc-text-secondary)]" />
      <span className="text-sm text-[var(--vidoc-text-secondary)] w-28">{label}</span>
      <span className="text-sm font-medium flex-1 break-all">{value}</span>
    </div>
  );
}
